import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { FormField, Input } from '@/components/ui/FormField';
import { Alert } from '@/components/ui/Alert';
import { useCreateSecondaryClaim, type Claim } from '@/api/claims';
import { useClient } from '@/api/clients';

/*
 * Create a COB secondary claim from a paid/partial primary. The server
 * snapshots primary-side adjudication into `cob`, mirrors service line fields,
 * and bills the primary PR balance (falling back to primary billed when PR=0).
 * We just collect the secondary payer + id + optional auth # override here.
 */

interface CreateSecondaryDialogProps {
    claim: Claim;
    secondaryPayerName?: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function CreateSecondaryDialog({
    claim, secondaryPayerName, open, onOpenChange,
}: CreateSecondaryDialogProps) {
    const navigate = useNavigate();
    const mutation = useCreateSecondaryClaim();
    const { data: client } = useClient(claim.clientId ?? undefined);

    const [payer, setPayer]           = useState(secondaryPayerName ?? '');
    const [payerId, setPayerId]       = useState('');
    const [authNumber, setAuthNumber] = useState('');

    useEffect(() => {
        if (open) {
            const ins2 = client?.ins2;
            setPayer(ins2?.payer ?? secondaryPayerName ?? '');
            setPayerId(ins2?.id ?? '');
            setAuthNumber('');
            mutation.reset();
        }
    }, [open, claim.id, client?.ins2]); // eslint-disable-line react-hooks/exhaustive-deps

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!payer.trim()) return;
        try {
            const created = await mutation.mutateAsync({
                id: claim.id,
                payload: {
                    secondaryPayer: payer.trim(),
                    secondaryPayerId: payerId.trim(),
                    authNumber: authNumber.trim(),
                },
            });
            onOpenChange(false);
            navigate(`/billing/${created.id}`);
        } catch {
            /* surfaced via mutation.error */
        }
    }

    const serverError = (() => {
        const e = mutation.error;
        if (!e) return null;
        if (e instanceof AxiosError) return e.response?.data?.error ?? 'Failed to create secondary claim';
        return 'Unexpected error';
    })();

    const hasSecondaryOnFile = !!client?.ins2?.payer;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Create Secondary Claim</DialogTitle>
                    <DialogDescription>
                        Bills the patient responsibility balance from {claim.claimNo} to the client's secondary payer.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={onSubmit} noValidate>
                    {serverError && <Alert variant="danger" className="mb-4">{serverError}</Alert>}

                    {!hasSecondaryOnFile && (
                        <Alert variant="warning" className="mb-4">
                            No secondary insurance is on file for this client. You can still create a
                            secondary claim by entering payer details manually.
                        </Alert>
                    )}

                    <FormField label="Secondary Payer" required>
                        {(id) => (
                            <Input
                                id={id}
                                value={payer}
                                onChange={(e) => setPayer(e.target.value)}
                                placeholder="e.g., Medicaid of State"
                            />
                        )}
                    </FormField>

                    <FormField label="Secondary Payer ID (EDI)">
                        {(id) => (
                            <Input
                                id={id}
                                value={payerId}
                                onChange={(e) => setPayerId(e.target.value)}
                            />
                        )}
                    </FormField>

                    <FormField
                        label="Authorization #"
                        hint="Optional — leave blank to reuse the primary's auth number."
                    >
                        {(id) => (
                            <Input
                                id={id}
                                value={authNumber}
                                onChange={(e) => setAuthNumber(e.target.value)}
                            />
                        )}
                    </FormField>

                    <div className="mt-3 p-3 bg-surface-muted rounded-md text-xs text-text-muted">
                        The service line, modifiers, diagnoses, and dates will be copied from the primary.
                        Billed amount will be the PR balance ({(claim.prBalance || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' })}),
                        falling back to the primary's billed amount if PR is zero.
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            disabled={mutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={mutation.isPending || !payer.trim()}
                        >
                            {mutation.isPending ? 'Creating…' : 'Create secondary'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
